import { useEffect, useState } from "react";
import { Pencil, RotateCcw, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import api from "../../lib/api";
import { useContent } from "../../context/ContentContext";

// Pencil badge shown only to a signed-in owner with Edit mode on. Absolutely positioned; parent gets `relative` via wrapper.
export function EditBadge({ onClick, testId, label = "Edit", className = "" }) {
  return (
    <span role="button" tabIndex={0} data-testid={testId} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(e); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onClick(e); } }}
      aria-label={label} title={label}
      className={`edit-badge inline-flex items-center gap-1 rounded-full tracking-normal normal-case bg-[#F0B429] text-black text-[11px] font-extrabold px-2 py-1 shadow-lg ring-2 ring-white hover:bg-[#ffc94d] ${className}`}>
      <Pencil size={11} aria-hidden="true" /> {label}
    </span>
  );
}

// Inline text editor. `k` = content key, `fallback` = the hard-coded default. Optional `onSave(value)` bypasses the content store
// (used for product/category/business fields, where the value lives on the entity).
export function Editable({ k, fallback = "", as: Tag = "span", className = "", multiline = false, onSave, value: forced, label, children, placeholder, testId, ...rest }) {
  const { get, save, editMode, applyRestored } = useContent();
  const current = forced !== undefined ? forced : get(k, fallback);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(current ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setDraft(current ?? ""); }, [open, current]);

  const commit = async () => {
    setBusy(true);
    try {
      if (onSave) await onSave(draft);
      else await save(k, draft, { label: label || k, type: multiline ? "richtext" : "text" });
      toast.success("Saved — live now");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };
  const undo = async () => {
    if (onSave) { toast.info("Use History in the edit bar to restore this item"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/cms/undo/content/${k}`);
      applyRestored(k, data.value);
      toast.success("Previous version restored");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to restore");
    } finally { setBusy(false); }
  };

  const shown = children !== undefined ? children : (current || placeholder || "");
  if (!editMode) return <Tag className={className} data-testid={testId} {...rest}>{shown}</Tag>;
  return (
    <Tag className={`${className} editable-outline relative`} data-testid={testId} data-edit-key={k} {...rest}>
      {shown}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <span className="absolute -top-3 -right-2 z-30"><EditBadge testId={`edit-${(k || label || "field").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => setOpen(true)} /></span>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[min(92vw,26rem)] p-3 space-y-2 z-[80]" onClick={(e) => e.stopPropagation()}>
          <p className="text-xs font-bold text-neutral-500 uppercase tracking-wider">{label || k}</p>
          {multiline ? (
            <textarea data-testid="editable-input" value={draft} onChange={(e) => setDraft(e.target.value)} rows={5}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-black" />
          ) : (
            <input data-testid="editable-input" value={draft} onChange={(e) => setDraft(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-black" />
          )}
          <div className="flex items-center justify-between gap-2">
            <button type="button" data-testid="editable-undo" onClick={undo} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-600 hover:text-black">
              <RotateCcw size={12} /> Restore last version
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOpen(false)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border"><X size={12} /> Cancel</button>
              <button type="button" data-testid="editable-save" onClick={commit} disabled={busy}
                className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-black text-white disabled:opacity-50"><Check size={12} /> Save</button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </Tag>
  );
}
